// src/pages/itsm/ITSMTickets.tsx
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, EmptyState, statusTone } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string }> = {
    open: { label: 'Open' },
    pending: { label: 'Pending' },
    in_progress: { label: 'In Progress' },
    pending_approval: { label: 'Pending Approval' },
    approved: { label: 'Approved' },
    rejected: { label: 'Rejected' },
    resolved: { label: 'Resolved' },
    closed: { label: 'Closed' },
}

const PRIORITY_CONFIG: Record<string, { label: string; tone: 'accent' | 'warning' | 'danger' | 'violet' }> = {
    low: { label: 'Low', tone: 'accent' },
    medium: { label: 'Medium', tone: 'warning' },
    high: { label: 'High', tone: 'danger' },
    urgent: { label: 'Urgent', tone: 'violet' },
}

export default function ITSMTickets() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [searchParams] = useSearchParams()
    const [tickets, setTickets] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
    const [priorityFilter, setPriorityFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')

    useEffect(() => { fetchTickets() }, [statusFilter, priorityFilter, typeFilter])

    const fetchTickets = async () => {
        let query = supabase
            .from('itsm_tickets')
            .select(`
                *,
                itsm_services(name),
                itsm_service_categories(name, icon, color),
                itsm_agents!assigned_agent_id(name, avatar),
                client_profiles!requester_id(full_name, company_name)
            `)
            .order('created_at', { ascending: false })

        if (statusFilter !== 'all') query = query.eq('status', statusFilter)
        if (priorityFilter !== 'all') query = query.eq('priority', priorityFilter)
        if (typeFilter !== 'all') query = query.eq('type', typeFilter)

        const { data } = await query
        setTickets(data || [])
        setLoading(false)
    }

    const filtered = tickets.filter(t =>
        t.title?.toLowerCase().includes(search.toLowerCase()) ||
        t.ticket_number?.toLowerCase().includes(search.toLowerCase()) ||
        t.requester_name?.toLowerCase().includes(search.toLowerCase())
    )

    const s = styles

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>
                <ArrowLeft size={13} /> Back to ITSM
            </button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Tickets</h1>
                    <p style={s.subtitle}>{filtered.length} tickets</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <button style={s.btnPrimary} onClick={() => navigate('/dashboard/itsm/tickets/new')}>
                        + New Ticket
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div style={s.filters}>
                <input
                    style={s.search}
                    placeholder="Search tickets..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                <select style={s.select} value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
                    <option value="all">All Priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                </select>
                <select style={s.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="all">All Types</option>
                    <option value="incident">Incident</option>
                    <option value="service_request">Service Request</option>
                    <option value="change">Change</option>
                    <option value="problem">Problem</option>
                </select>
            </div>

            {/* Table */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            {['#', 'Title', 'Type', 'Service', 'Requester', 'Agent', 'Priority', 'Status', 'SLA', 'Created'].map(h => (
                                <th key={h} style={s.th}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(t => {
                            const status = STATUS_CONFIG[t.status] || STATUS_CONFIG.open
                            const priority = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.medium
                            const isBreached = t.sla_breached
                            const slaHours = t.sla_due_at ? Math.round((new Date(t.sla_due_at).getTime() - Date.now()) / 3600000) : null

                            return (
                                <tr key={t.id} style={s.tr} onClick={() => navigate(`/dashboard/itsm/tickets/${t.id}`)}>
                                    <td style={s.td}>
                                        <span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 600 }}>{t.ticket_number}</span>
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{t.title}</span>
                                    </td>
                                    <td style={s.td}>
                                        <Pill tone="neutral" icon={undefined} style={undefined}>{t.type?.replace('_', ' ').toUpperCase()}</Pill>
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 12, color: T.text2 }}>
                                            {t.itsm_service_categories?.icon} {t.itsm_services?.name || '—'}
                                        </span>
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 12, color: T.text2 }}>
                                            {t.client_profiles?.full_name || t.requester_name || '—'}
                                        </span>
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 12, color: t.itsm_agents ? T.text2 : T.text3 }}>
                                            {t.itsm_agents?.name || 'Unassigned'}
                                        </span>
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: priority.tone === 'accent' ? T.text2 : (T as any)[priority.tone] }}>
                                            {priority.label}
                                        </span>
                                    </td>
                                    <td style={s.td}>
                                        <Pill tone={statusTone(t.status)} icon={undefined} style={undefined}>{status.label}</Pill>
                                    </td>
                                    <td style={s.td}>
                                        {isBreached ? (
                                            <span style={{ fontSize: 11, color: T.danger, fontWeight: 700 }}>BREACHED</span>
                                        ) : slaHours !== null ? (
                                            <span style={{ fontSize: 11, color: slaHours < 2 ? T.warning : T.success }}>
                                                {slaHours}h left
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td style={s.td}>
                                        <span style={{ fontSize: 11, color: T.text3 }}>
                                            {new Date(t.created_at).toLocaleDateString('en-GB')}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                {!loading && filtered.length === 0 && (
                    <EmptyState title="No tickets found" icon={undefined} sub={undefined} />
                )}
            </Card>
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1300, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    filters: { display: 'flex', gap: 10, marginBottom: 16 },
    search: { flex: 1, height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', background: T.surface, color: T.text },
    select: { height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', background: T.surface, color: T.text2 },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const, whiteSpace: 'nowrap' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'middle' as const },
    tr: { cursor: 'pointer' },
    btnPrimary: { height: 36, padding: '0 18px', background: T.accent, color: T.accentFg, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
}
